import { Box, Modal, Tab, Tabs } from '@mui/material';
import { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { DataGrid } from '@mui/x-data-grid';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { InputAdornment, TextField } from '@mui/material';
import { Search } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import TabPanel from '@/components/ui/Tab/TabPanel';
import Pagination from '@/components/ui/Pagination/Pagination';
import BaseSelect from '@/components/ui/Select/BaseSelect';
import BaseInputValidation from '@/components/ui/Input/BaseInputValidation';
import { BaseInputPassword } from '@/components/ui/Input/BaseInputPassword';
import ButtonCloseModal from '@/components/ui/Button/ButtonCloseModal';

import { a11yProps } from '@/helper/a11yProps';
import ModalStyle from '@/constants/ModalStyle';
import usersData from '@/assets/data/Users.json';

const tabItems = [{ name: 'Søk bruker' }, { name: 'Legg til midlertidig bruker' }];

const schema = yup.object().shape({
	privilege: yup.array().required(),
	email: yup.string().required('Email is a required field').email('Email not valid'),
	password: yup.string().required(),
	surname: yup.string().required(),
	location: yup.string().required(),
	department: yup.string().required(),
	username: yup.string().required(),
	firstName: yup.string().required(),
	phone: yup.string().required(),
});

export default function Share(props) {
	const { open, onClose } = props;

	const [activeTab, setActiveTab] = useState(0);
	const [s, setSearch] = useState('');
	const [search] = useDebounce(s, 300);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

	const onTabChange = (_, newValue) => {
		setActiveTab(newValue);
	};

	const { data: d, isFetching } = useQuery({
		queryKey: ['users', search],
		queryFn: () => usersData,
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const dataList = useMemo(() => d, [d]) || [];

	const columns = [
		{
			field: 'id',
			headerName: 'Nr',
			filterable: false,
			sortable: true,
			renderCell: (index) => index.api.getRowIndexRelativeToVisibleRows(index.row.id + 1),
			width: 32,
		},
		{
			field: 'username',
			headerName: 'Navn',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
		},
		{
			field: 'email',
			headerName: 'E-post',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
		},
		{
			field: 'phonenumber',
			headerName: 'Telefonnummer',
			headerAlign: 'center',
			align: 'center',
			flex: 1,
		},
		{
			field: 'modify_date',
			headerName: 'Oppdatert dato',
			headerAlign: 'center',
			align: 'center',
		},
		{
			field: 'privilegeLevelID',
			headerName: 'Rolle',
			headerAlign: 'center',
			align: 'center',
		},
	];

	const {
		handleSubmit,
		control,
		reset,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {},
	});

	const onSubmit = () => {
		onClose();
		reset();
	};

	return (
		<Modal open={open} onClose={onClose} aria-labelledby='modal-project-qr' aria-describedby='modal-project-qr' className='w-full'>
			<Box sx={{ ...ModalStyle, top: '50vh' }} className='relative flex flex-col gap-4 min-w-[768px] w-auto h-auto'>
				<div className='flex flex-col items-center justify-center flex-1 w-full h-auto gap-4 text-center'>
					<ButtonCloseModal onClose={onClose} />

					<hr />

					<Tabs value={activeTab} onChange={onTabChange} aria-label='share tabs' className='w-full mt-4 bd-tabs'>
						{tabItems.map((tb, index) => (
							<Tab key={tb.name} className='w-full p-4' name={tb.url} label={tb.name} {...a11yProps(tb.name, index)} />
						))}
					</Tabs>
					<TabPanel value={activeTab} index={0} name={tabItems[0].name}>
						<div className='flex flex-col w-full gap-4'>
							<TextField
								id='search'
								className='w-full input-search'
								placeholder='Søk'
								onChange={(e) => {
									setSearch(e.target.value);
								}}
								size='small'
								InputProps={{
									startAdornment: (
										<InputAdornment position='start'>
											<Search style={{ color: 'black' }} />
										</InputAdornment>
									),
								}}
							/>
							<DataGrid
								autoHeight
								rows={dataList}
								loading={isFetching}
								columns={columns}
								// rowsPerPageOptions={[10, 20, 50]}
								className='mx-auto lg:max-w-[1600px] w-full h-full mt-0'
								disableColumnFilter
								disableColumnSelector
								disableDensitySelector
								disableToolbarButton
								disableRowSelectionOnClick
								slots={{
									pagination: Pagination,
								}}
								getRowClassName={(params) => {
									let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
									// if (selectedRow?.id === params.row.id) {
									// 	cellClass += ' font-bold border border-black';
									// }
									return cellClass;
								}}
								slotProps={{
									loadingOverlay: {
										variant: 'skeleton',
										noRowsVariant: 'skeleton',
									},
									pagination: {
										page: paginationModel.page,
									},
								}}
								onCellClick={(params, event) => {
									if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
								}}
								initialState={{
									pagination: { paginationModel: { pageSize: 10 } },
								}}
								paginationModel={paginationModel}
								onPaginationModelChange={(obj) => setPaginationModel(obj)}
							/>
						</div>
					</TabPanel>
					<TabPanel value={activeTab} index={1} name={tabItems[1].name} className='w-full'>
						<form onSubmit={handleSubmit(onSubmit)} className='flex flex-col w-full gap-4'>
							<div className='flex flex-row w-full gap-4'>
								<div className='flex flex-col w-full gap-4'>
									<BaseSelect
										control={control}
										name='privilege'
										label='Kontotype'
										errors={errors}
										data={[
											{ id: 2, name: 'Administrasjon', value: '2' },
											{ id: 3, name: 'Produktsjef', value: '3' },
											{ id: 4, name: 'Prosjektleder', value: '4' },
											{ id: 5, name: 'Ansatt', value: '5' },
											{ id: 6, name: 'Public', value: '6' },
											{ id: 7, name: 'Ekstern bruker', value: '7' },
											{ id: 8, name: 'Read only', value: '8' },
										]}
										config={{ styles: {}, classes: '', optionLabel: '--Select Status--' }}
										size='small'
									/>
									<BaseInputValidation control={control} name='email' label='E-post' config={{ classes: '' }} errors={errors} required />
									<BaseInputPassword name='password' label='Passord' control={control} errors={errors} />
								</div>
								<div className='flex flex-col w-full gap-4'>
									<BaseInputValidation
										control={control}
										name='department'
										label='Avdeling'
										config={{ classes: '' }}
										errors={errors}
										required
									/>
									<BaseInputValidation
										control={control}
										name='username'
										label='Brukernavn'
										config={{ classes: '' }}
										errors={errors}
										required
									/>
								</div>
							</div>
							<div className='flex flex-row w-full gap-4'>
								<div className='flex flex-col w-full gap-4'>
									<BaseInputValidation
										control={control}
										name='surname'
										label='Etternavnet'
										config={{ classes: '' }}
										errors={errors}
										required
									/>
									<BaseInputValidation
										control={control}
										name='location'
										label='Plassering'
										config={{ classes: '' }}
										errors={errors}
										required
									/>
								</div>
								<div className='flex flex-col w-full gap-4'>
									<BaseInputValidation
										control={control}
										name='firstName'
										label='Fornavn'
										config={{ classes: '' }}
										errors={errors}
										required
									/>
									<BaseInputValidation
										control={control}
										name='phone'
										label='Telefonnummer'
										config={{ classes: '' }}
										errors={errors}
										required
									/>
								</div>
							</div>
						</form>
					</TabPanel>

					<div className='flex gap-4 ml-auto'>
						<button className='px-3 py-2 text-white rounded-md btn bg-primary' type='submit' onClick={onClose}>
							Legg til
						</button>
					</div>
				</div>
			</Box>
		</Modal>
	);
}
